import {
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
	LinkOutlined,
	PlusOutlined,
	ReloadOutlined,
	StopOutlined,
	UnlockOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Checkbox,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Switch,
	Table,
	Tag,
	Tooltip,
	Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';

import {
	LimitedEditionImportErrorDto,
	LimitedEditionImportResultDto,
	LimitedEditionNumberDto,
	LimitedEditionReleasePreviewDto,
	LimitedEditionSearchParams,
	LimitedEditionStatus,
	LimitedEditionWriteDto,
} from '../../../../api-client';

type BVLimitedEditionsTableProps = {
	loading: boolean;
	numbers: LimitedEditionNumberDto[];
	total: number;
	onRefresh: (params: LimitedEditionSearchParams) => Promise<void>;
	onSave: (number: LimitedEditionWriteDto, id?: number) => Promise<boolean>;
	saving: boolean;
	onRemove: (id: number) => Promise<void>;
	removing: boolean;
	onRelease: (id: number) => Promise<void>;
	releasing: boolean;
	onBlock: (id: number, reason?: string | null) => Promise<void>;
	blocking: boolean;
	onAssign: (id: number, orderId: number, rowId?: number | null) => Promise<boolean>;
	assigning: boolean;
	onPreviewRelease: (sapCode: string) => Promise<LimitedEditionReleasePreviewDto | null>;
	onReleaseBySapCode: (
		sapCode: string,
		includeConfirmedOrders: boolean,
	) => Promise<boolean>;
	releasingBySapCode: boolean;
	onExport: () => Promise<void>;
	onExportTemplate: () => Promise<void>;
	exporting: boolean;
	onImport: (file: File | Blob) => Promise<void>;
	importing: boolean;
	importResult: LimitedEditionImportResultDto | null;
	onClearImportResult: () => void;
};

const emptyForm: LimitedEditionWriteDto = {
	sapCode: '',
	number: '',
	available: true,
	note: '',
};

const statusOptions = [
	{ value: 'Available', label: 'Available' },
	{ value: 'Reserved', label: 'Reserved' },
	{ value: 'Blocked', label: 'Blocked' },
];

const statusTag = (row: LimitedEditionNumberDto) => {
	switch (row.status) {
		case 'Reserved':
			return <Tag color="blue">Reserved</Tag>;
		case 'Blocked':
			return (
				<Tooltip title={row.blockedReason ?? 'Flagged as not available'}>
					<Tag color="red">Blocked</Tag>
				</Tooltip>
			);
		default:
			return <Tag color="green">Available</Tag>;
	}
};

const BVLimitedEditionsTable = ({
	loading,
	numbers,
	total,
	onRefresh,
	onSave,
	saving,
	onRemove,
	removing,
	onRelease,
	releasing,
	onBlock,
	blocking,
	onAssign,
	assigning,
	onPreviewRelease,
	onReleaseBySapCode,
	releasingBySapCode,
	onExport,
	onExportTemplate,
	exporting,
	onImport,
	importing,
	importResult,
	onClearImportResult,
}: BVLimitedEditionsTableProps) => {
	const [sapCodeFilter, setSapCodeFilter] = useState<string>('');
	const [numberFilter, setNumberFilter] = useState<string>('');
	const [statusFilter, setStatusFilter] = useState<LimitedEditionStatus | undefined>(
		undefined,
	);
	const [orderIdFilter, setOrderIdFilter] = useState<number | undefined>(undefined);

	// Server side paging: a single product can carry five hundred numbers, so the whole table is never
	// loaded to be paged in the browser.
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<LimitedEditionNumberDto | null>(null);
	const [form, setForm] = useState<LimitedEditionWriteDto>(emptyForm);

	const [blockTarget, setBlockTarget] = useState<LimitedEditionNumberDto | null>(null);
	const [blockReason, setBlockReason] = useState<string>('');

	const [assignTarget, setAssignTarget] = useState<LimitedEditionNumberDto | null>(
		null,
	);
	const [assignOrderId, setAssignOrderId] = useState<number | undefined>(undefined);
	const [assignRowId, setAssignRowId] = useState<number | undefined>(undefined);

	const [releasePreview, setReleasePreview] =
		useState<LimitedEditionReleasePreviewDto | null>(null);
	const [releaseIncludeConfirmed, setReleaseIncludeConfirmed] = useState(false);

	const currentParams = (targetPage = page, targetPageSize = pageSize) => {
		const sapCode = sapCodeFilter.trim();
		const number = numberFilter.trim();

		return {
			sapCode: sapCode ? sapCode : undefined,
			number: number ? number : undefined,
			status: statusFilter,
			orderId: orderIdFilter,
			skip: (targetPage - 1) * targetPageSize,
			take: targetPageSize,
		};
	};

	useEffect(() => {
		onRefresh(currentParams(1, 20));
	}, []);

	const handleRefresh = async (targetPage = page, targetPageSize = pageSize) => {
		setPage(targetPage);
		setPageSize(targetPageSize);
		await onRefresh(currentParams(targetPage, targetPageSize));
	};

	/** A filter change goes back to the first page: staying on page seven of the previous result is noise. */
	const handleFilter = () => handleRefresh(1, pageSize);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setFormOpen(true);
	};

	const openEdit = (row: LimitedEditionNumberDto) => {
		setEditing(row);
		setForm({
			sapCode: row.sapCode,
			number: row.number,
			available: row.available,
			blockedReason: row.blockedReason ?? null,
			note: row.note ?? '',
		});
		setFormOpen(true);
	};

	const handleSave = async () => {
		const saved = await onSave(form, editing?.id);
		if (!saved) return;

		setFormOpen(false);
		await handleRefresh();
	};

	const handleBlock = async () => {
		if (!blockTarget) return;

		await onBlock(blockTarget.id, blockReason.trim() ? blockReason.trim() : null);

		setBlockTarget(null);
		setBlockReason('');
		await handleRefresh();
	};

	const handleAssign = async () => {
		if (!assignTarget || !assignOrderId) return;

		const assigned = await onAssign(assignTarget.id, assignOrderId, assignRowId);
		if (!assigned) return;

		setAssignTarget(null);
		setAssignOrderId(undefined);
		setAssignRowId(undefined);
		await handleRefresh();
	};

	/**
	 * The dialog is opened only with the counts in hand: without them it would be asking to release an
	 * unknown number of pieces.
	 */
	const openRelease = async () => {
		const sapCode = sapCodeFilter.trim();
		if (!sapCode) return;

		const preview = await onPreviewRelease(sapCode);
		if (!preview) return;

		setReleaseIncludeConfirmed(false);
		setReleasePreview(preview);
	};

	const releaseCount = releasePreview
		? releasePreview.heldByDraftsCount +
			(releaseIncludeConfirmed ? releasePreview.heldByConfirmedOrdersCount : 0)
		: 0;

	const handleRelease = async () => {
		if (!releasePreview?.sapCode) return;

		const released = await onReleaseBySapCode(
			releasePreview.sapCode,
			releaseIncludeConfirmed,
		);
		if (!released) return;

		setReleasePreview(null);
		await handleRefresh(1, pageSize);
	};

	const columns: ColumnsType<LimitedEditionNumberDto> = useMemo(
		() => [
			{ title: 'SAP Code', dataIndex: 'sapCode', key: 'sapCode' },
			{ title: 'Number', dataIndex: 'number', key: 'number' },
			{
				title: 'Status',
				key: 'status',
				width: 110,
				render: (_v, r) => statusTag(r),
			},
			{
				title: 'Order',
				key: 'order',
				width: 110,
				render: (_v, r) => (r.idOrder ? `#${r.idOrder}` : '-'),
			},
			{
				title: 'Order status',
				key: 'orderStatus',
				width: 120,
				// Draft means the cart is still open: the number is committed but nothing has been sent.
				render: (_v, r) =>
					r.orderStatusCode ? (
						<Tag color={r.orderStatusCode === 'Draft' ? 'gold' : 'geekblue'}>
							{r.orderStatusCode}
						</Tag>
					) : (
						'-'
					),
			},
			{
				title: 'Customer',
				key: 'customer',
				render: (_v, r) =>
					r.customerName
						? `${r.customerName}${r.customerSapCode ? ` (${r.customerSapCode})` : ''}`
						: '-',
			},
			{
				title: 'Note',
				dataIndex: 'note',
				key: 'note',
				render: (v?: string | null) => v || '-',
			},
			{
				title: 'Last update',
				key: 'updated',
				render: (_v, r) =>
					`${new Date(r.updatedAt).toLocaleString()}${
						r.updatedBy ? ` by ${r.updatedBy}` : ''
					}`,
			},
			{
				title: '',
				key: 'actions',
				width: 200,
				align: 'right',
				render: (_v, r) => {
					const isReserved = r.status === 'Reserved';

					return (
						<div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
							<Tooltip title="Edit">
								<Button
									aria-label={`Edit ${r.number}`}
									icon={<EditOutlined />}
									size="small"
									onClick={() => openEdit(r)}
								/>
							</Tooltip>

							{isReserved ? (
								<Popconfirm
									title={`Release the number from order #${r.idOrder}? It goes back in the pool, and that order keeps it on its rows: it will be refused at confirmation until the agent picks another one.`}
									okText="Release"
									cancelText="Cancel"
									onConfirm={async () => {
										await onRelease(r.id);
										await handleRefresh();
									}}
								>
									<Tooltip title="Release from its order">
										<Button
											aria-label={`Release ${r.number}`}
											icon={<UnlockOutlined />}
											size="small"
											loading={releasing}
										/>
									</Tooltip>
								</Popconfirm>
							) : (
								<Tooltip
									title={
										r.blockedReason
											? 'Put the number back in the pool'
											: 'Keep the number out of the pool'
									}
								>
									<Button
										aria-label={`Block ${r.number}`}
										icon={<StopOutlined />}
										size="small"
										loading={blocking}
										onClick={() => {
											setBlockTarget(r);
											setBlockReason(r.blockedReason ?? '');
										}}
									/>
								</Tooltip>
							)}

							<Tooltip
								title={
									isReserved
										? 'Already held by an order: release it first'
										: 'Assign to an order by hand'
								}
							>
								<Button
									aria-label={`Assign ${r.number}`}
									icon={<LinkOutlined />}
									size="small"
									disabled={isReserved}
									loading={assigning}
									onClick={() => {
										setAssignTarget(r);
										setAssignOrderId(undefined);
										setAssignRowId(undefined);
									}}
								/>
							</Tooltip>

							<Popconfirm
								title="Delete this number? It disappears from the list and can no longer be picked."
								okText="Delete"
								cancelText="Cancel"
								disabled={isReserved}
								onConfirm={async () => {
									await onRemove(r.id);
									await handleRefresh();
								}}
							>
								<Tooltip
									title={
										isReserved
											? 'Held by an order: release it first'
											: 'Delete'
									}
								>
									<Button
										aria-label={`Delete ${r.number}`}
										icon={<DeleteOutlined />}
										danger
										size="small"
										disabled={isReserved}
										loading={removing}
									/>
								</Tooltip>
							</Popconfirm>
						</div>
					);
				},
			},
		],
		[removing, releasing, blocking, assigning, page, pageSize],
	);

	const importReportColumns: ColumnsType<LimitedEditionImportErrorDto> = [
		{ title: 'Row', dataIndex: 'rowNumber', key: 'rowNumber', width: 80 },
		{
			title: 'SAP Code',
			dataIndex: 'sapCode',
			key: 'sapCode',
			render: (v?: string | null) => v || '-',
		},
		{
			title: 'Number',
			dataIndex: 'number',
			key: 'number',
			render: (v?: string | null) => v || '-',
		},
		{ title: 'Reason', dataIndex: 'reason', key: 'reason' },
	];

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<label className="admin-label">{`Limited edition numbers (${total})`}</label>

				<div
					style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
				>
					<Input
						style={{ minWidth: 180 }}
						placeholder="Filter by SAP code"
						value={sapCodeFilter}
						allowClear
						onChange={(e) => setSapCodeFilter(e.target.value)}
						onPressEnter={handleFilter}
					/>

					<Input
						style={{ minWidth: 160 }}
						placeholder="Filter by number"
						value={numberFilter}
						allowClear
						onChange={(e) => setNumberFilter(e.target.value)}
						onPressEnter={handleFilter}
					/>

					<Select
						style={{ minWidth: 160 }}
						placeholder="Filter by status"
						options={statusOptions}
						value={statusFilter}
						allowClear
						onChange={(value) => setStatusFilter(value)}
					/>

					<InputNumber
						style={{ minWidth: 140 }}
						placeholder="Filter by order"
						value={orderIdFilter}
						min={1}
						onChange={(value) => setOrderIdFilter(value ?? undefined)}
						onPressEnter={handleFilter}
					/>

					<Button
						icon={<ReloadOutlined />}
						onClick={handleFilter}
						loading={loading}
					>
						Refresh
					</Button>

					<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
						New number
					</Button>

					<Upload
						accept=".xlsx"
						showUploadList={false}
						beforeUpload={async (file) => {
							await onImport(file);
							await handleRefresh(1, pageSize);
							return false;
						}}
					>
						<Button icon={<UploadOutlined />} loading={importing}>
							Import
						</Button>
					</Upload>

					<Button
						icon={<DownloadOutlined />}
						onClick={onExport}
						loading={exporting}
					>
						Export
					</Button>

					<Button icon={<DownloadOutlined />} onClick={onExportTemplate}>
						Template
					</Button>

					{/* Only for one sap code at a time: there is no "release everything" on purpose. */}
					<Tooltip
						title={
							sapCodeFilter.trim()
								? 'Put the numbers of this sap code back in circulation'
								: 'Filter by sap code first: the release works on one pool at a time'
						}
					>
						<Button
							icon={<UnlockOutlined />}
							danger
							disabled={!sapCodeFilter.trim()}
							loading={releasingBySapCode}
							onClick={openRelease}
						>
							Release pool
						</Button>
					</Tooltip>
				</div>
			</div>

			{importResult && importResult.skipped > 0 && (
				<Alert
					style={{ marginTop: 12 }}
					type="warning"
					closable
					onClose={onClearImportResult}
					message={`Import report: ${importResult.created} created, ${importResult.updated} updated, ${importResult.skipped} skipped out of ${importResult.totalRows} rows.`}
					description={
						<Table<LimitedEditionImportErrorDto>
							style={{ marginTop: 8 }}
							rowKey={(r) => `${r.rowNumber}-${r.number ?? ''}`}
							columns={importReportColumns}
							dataSource={importResult.errors}
							pagination={{ pageSize: 10 }}
							size="small"
						/>
					}
				/>
			)}

			{importResult && importResult.warnings?.length > 0 && (
				<Alert
					style={{ marginTop: 12 }}
					type="info"
					closable
					onClose={onClearImportResult}
					message={`${importResult.warnings.length} numbers are held by an order: availability and note were updated, the assignment was left as it is.`}
					description={
						<Table<LimitedEditionImportErrorDto>
							style={{ marginTop: 8 }}
							rowKey={(r) => `warning-${r.rowNumber}-${r.number ?? ''}`}
							columns={importReportColumns}
							dataSource={importResult.warnings}
							pagination={{ pageSize: 5 }}
							size="small"
						/>
					}
				/>
			)}

			<Table<LimitedEditionNumberDto>
				style={{ marginTop: 12 }}
				rowKey={(r) => r.id}
				loading={loading}
				dataSource={numbers}
				columns={columns}
				pagination={{
					current: page,
					pageSize,
					total,
					showSizeChanger: true,
					pageSizeOptions: ['20', '50', '100'],
					onChange: (nextPage, nextPageSize) =>
						handleRefresh(nextPage, nextPageSize ?? pageSize),
				}}
			/>

			<Modal
				title={editing ? 'Edit number' : 'New number'}
				visible={formOpen}
				onCancel={() => setFormOpen(false)}
				onOk={handleSave}
				okText="Save"
				confirmLoading={saving}
				okButtonProps={{
					disabled: !form.sapCode.trim() || !form.number.trim(),
				}}
			>
				<Form layout="vertical">
					<Form.Item
						label="SAP code"
						required
						help="The code must exist on a product size, otherwise the save is refused."
					>
						<Input
							value={form.sapCode}
							onChange={(e) => setForm({ ...form, sapCode: e.target.value })}
						/>
					</Form.Item>

					<Form.Item
						label="Number"
						required
						help="The number as it has to reach SAP, for instance 12/50."
					>
						<Input
							value={form.number}
							onChange={(e) => setForm({ ...form, number: e.target.value })}
						/>
					</Form.Item>

					<Form.Item
						label="Available"
						help="Off keeps the number out of the pool, like the import flag."
					>
						<Switch
							checked={form.available}
							onChange={(checked) => setForm({ ...form, available: checked })}
						/>
					</Form.Item>

					<Form.Item label="Note">
						<Input.TextArea
							rows={2}
							value={form.note ?? ''}
							onChange={(e) => setForm({ ...form, note: e.target.value })}
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={
					blockTarget?.blockedReason
						? `Put number ${blockTarget?.number} back in the pool`
						: `Keep number ${blockTarget?.number} out of the pool`
				}
				visible={!!blockTarget}
				onCancel={() => {
					setBlockTarget(null);
					setBlockReason('');
				}}
				onOk={handleBlock}
				okText={blockTarget?.blockedReason && !blockReason.trim() ? 'Unblock' : 'Block'}
				confirmLoading={blocking}
			>
				<Form layout="vertical">
					<Form.Item
						label="Reason"
						help="Clearing the reason puts the number back in the pool."
					>
						<Input.TextArea
							rows={2}
							placeholder="Damaged piece, kept for an internal use, ..."
							value={blockReason}
							onChange={(e) => setBlockReason(e.target.value)}
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={`Put the numbers of ${releasePreview?.sapCode} back in circulation?`}
				visible={!!releasePreview}
				onCancel={() => setReleasePreview(null)}
				onOk={handleRelease}
				okText={`Release ${releaseCount} ${releaseCount === 1 ? 'number' : 'numbers'}`}
				okButtonProps={{ danger: true, disabled: releaseCount === 0 }}
				cancelText="Cancel"
				confirmLoading={releasingBySapCode}
				width={620}
			>
				<p>
					{`This sap code has ${releasePreview?.totalCount ?? 0} numbers: `}
					{`${releasePreview?.availableCount ?? 0} free, `}
					{`${releasePreview?.heldByDraftsCount ?? 0} held by orders still in draft, `}
					{`${releasePreview?.heldByConfirmedOrdersCount ?? 0} held by confirmed or sent orders, `}
					{`${releasePreview?.blockedCount ?? 0} blocked.`}
				</p>

				<p>
					<b>What happens:</b> the numbers are detached from their order and become selectable
					again, with a note of who released them and when.
				</p>

				<p>
					<b>What does not happen:</b> the orders are not touched, so an order that was holding one
					keeps it written on its rows and will be refused at confirmation until the agent picks
					another one; the numbers kept out of the pool by hand stay blocked; nothing is deleted.
				</p>

				<Checkbox
					checked={releaseIncludeConfirmed}
					disabled={(releasePreview?.heldByConfirmedOrdersCount ?? 0) === 0}
					onChange={(e) => setReleaseIncludeConfirmed(e.target.checked)}
				>
					{`Include the ${releasePreview?.heldByConfirmedOrdersCount ?? 0} numbers held by confirmed or sent orders`}
				</Checkbox>

				<p style={{ marginTop: 12, color: 'rgb(165, 40, 45)' }}>
					Detaching a number from an order already gone to SAP loses the link between the piece and
					the sale. This cannot be undone.
				</p>
			</Modal>

			<Modal
				title={`Assign number ${assignTarget?.number} to an order`}
				visible={!!assignTarget}
				onCancel={() => {
					setAssignTarget(null);
					setAssignOrderId(undefined);
					setAssignRowId(undefined);
				}}
				onOk={handleAssign}
				okText="Assign"
				confirmLoading={assigning}
				okButtonProps={{ disabled: !assignOrderId }}
			>
				<Form layout="vertical">
					<Form.Item
						label="Order"
						required
						help="The order must exist. Its rows are not touched: this only records who holds the piece."
					>
						<InputNumber
							style={{ width: '100%' }}
							min={1}
							value={assignOrderId}
							onChange={(value) => setAssignOrderId(value ?? undefined)}
						/>
					</Form.Item>

					<Form.Item label="Cart row" help="Optional, when the row is known.">
						<InputNumber
							style={{ width: '100%' }}
							min={0}
							value={assignRowId}
							onChange={(value) => setAssignRowId(value ?? undefined)}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default BVLimitedEditionsTable;
